<template>
  <div class="page-shell">
    <p class="mobile-page-title font-helvetica-700 font-bold text-gray-dark lg:text-[40px] lg:uppercase">Финансы</p>
    <section
      v-if="hasPdz"
      class="flex w-full flex-col gap-3 rounded-[20px] border border-orange/20 bg-warning-soft px-5 py-5 text-gray-dark"
    >
      <div class="flex flex-wrap items-end justify-between gap-3">
        <div class="flex flex-col gap-1">
          <span class="text-[15px] font-helvetica-700 font-bold uppercase tracking-[0.04em] text-orange">
            ПДЗ
          </span>
          <p class="text-[16px] font-helvetica-400 text-extra-text-color">
            Просроченная дебиторская задолженность
          </p>
        </div>
        <p class="text-[24px] font-helvetica-700 font-bold text-orange xl:text-[28px]">
          {{ formattedPdz }}
        </p>
      </div>
    </section>
    <FinanceDateFilter v-model="dateRange" @show="fetchBalance" />
    <FinanceTable :payload="payload || undefined" :loading="loading" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { useNuxtApp } from "#imports";
import FinanceDateFilter from "~/components/finance/FinanceDateFilter.vue";
import FinanceTable from "~/components/finance/FinanceTable.vue";
import { useClientPdz } from "~/composables/useClientPdz";

usePageTitle("Финансы");

type Range = { from: string | null; to: string | null };
type FinanceEntry = {
  Договор?: string;
  Организация?: string;
  СуммаНачальныйОстаток?: string | number;
  СуммаКонечныйОстаток?: string | number;
  ВидДокумента?: string;
  СуммаРасход?: string | number;
  СуммаПриход?: string | number;
  НомерДокумента?: string;
  ДатаДокумента?: string;
  ИдДокумента?: string;
  СсылкаДокумента?: string;
};

type FinancePayload = {
  Статус?: string;
  ОписаниеОшибки?: string;
  ОбщийБалансНаНачалоПериода?: string | number;
  ОбщийБалансНаКонецПериода?: string | number;
  Данные?: FinanceEntry[];
};

const dateRange = ref<Range>({
  from: "2020-01-01",
  to: "2025-12-31",
});

const payload = ref<FinancePayload | null>(null);
const loading = ref(false);
const { hasPdz, formattedPdz, fetchPdz } = useClientPdz();

const nuxtApp = useNuxtApp();
const $useOneCApi = nuxtApp.$useOneCApi as (
  action: string,
  payload?: Record<string, any>
) => Promise<any>;

const splitIso = (value: string) => {
  const [year, month, day] = value.split("-");
  return {
    year: year || "2020",
    month: month || "1",
    day: day || "1",
  };
};

const fetchBalance = async () => {
  if (typeof $useOneCApi !== "function") return;
  payload.value = null;
  loading.value = true;
  try {
    const fromValue = dateRange.value.from || "2020-01-01";
    const toValue = dateRange.value.to || "2025-12-31";
    const from = splitIso(fromValue);
    const to = splitIso(toValue);

    const res = await $useOneCApi("getBalanceDetails", {
      from_day: String(Number(from.day)),
      from_month: String(Number(from.month)),
      from_year: String(Number(from.year)),
      to_day: String(Number(to.day)),
      to_month: String(Number(to.month)),
      to_year: String(Number(to.year)),
    });

    const status = res?.["Статус"];
    if (status && status !== "OK") {
      throw new Error(res?.["ОписаниеОшибки"] || "Ошибка получения баланса");
    }

    payload.value = res ?? null;
  } catch (error) {
    console.error("Не удалось загрузить баланс", error);
    payload.value = null;
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchBalance();
  fetchPdz();
});
</script>
